import {
  AutoComplete,
  Button,
  DatePicker,
  Divider,
  Form,
  Input,
  InputNumber,
  Row,
  Space,
  Typography,
  message,
} from 'antd';
import classNames from 'classnames/bind';
import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import API from 'helpers/API';
import { getAddress } from 'utils/AddressFinder';
import { BottomButtons, HeadingH1 } from 'components/ui';
import { getApiErrorMessage } from 'utils/getApiErrorMessage';
import { createOrderDateValue, formatOrderDateValue } from './dateFields';
import {
  buildCustomerCompanyOptions,
  buildCustomerNameOptions,
  buildOrderFieldOptions,
  findCustomerUserByName,
} from './autocompleteOptions';

import styles from './new.module.scss';

const cx = classNames.bind(styles);

const createAutocompleteKeyDownHandler =
  (form, fieldName, options, onResolved) => (event) => {
  if (event.key !== 'Enter') {
    return;
  }

  // 한글 IME 조합 중 Enter는 글자 확정용으로 먼저 소비되므로
  // 자동완성 확정을 섞으면 마지막 글자가 중복 입력될 수 있다.
  if (event.nativeEvent?.isComposing || event.keyCode === 229) {
    return;
  }

  if ((options ?? []).length !== 1) {
    return;
  }

  const onlyOptionValue = options[0]?.value;
  const currentValue = form.getFieldValue(fieldName);

  if (!onlyOptionValue) {
    return;
  }

  if (currentValue !== onlyOptionValue) {
    event.preventDefault();
    form.setFieldValue(fieldName, onlyOptionValue);
  }

  onResolved?.(onlyOptionValue);
};

const Page = () => {
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const [orders, setOrders] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [users, setUsers] = useState([]);
  const [searchTerms, setSearchTerms] = useState({
    customerCompany: '',
    customerName: '',
    deliveryName: '',
    deliveryCompany: '',
  });

  useEffect(() => {
    form.setFieldValue('orderDate', createOrderDateValue());

    Promise.all([API.get('/orders'), API.get('/customers'), API.get('/users')]).then(
      ([ordersResponse, customersResponse, usersResponse]) => {
        if (ordersResponse?.success) {
          setOrders(ordersResponse.data.orders ?? []);
        }
        if (customersResponse?.success) {
          setCustomers(customersResponse.data.customers ?? []);
        }
        if (usersResponse?.success) {
          setUsers(usersResponse.data.users ?? []);
        }
      },
    );
  }, [form]);

  const resolveCustomerPhone = (matchedOrder) => {
    if (matchedOrder?.customerPhone) {
      return matchedOrder.customerPhone;
    }

    const customerTel = (customers ?? []).find(
      (customer) =>
        String(customer?.name ?? '').trim() ===
        String(matchedOrder?.customerCompany ?? '').trim(),
    )?.tel;

    if (customerTel) {
      return customerTel;
    }

    const userPhone =
      (users ?? []).find(
        (user) =>
          String(user?.name ?? '').trim() ===
            String(matchedOrder?.customerName ?? '').trim() &&
          String(user?.customer?.name ?? '').trim() ===
            String(matchedOrder?.customerCompany ?? '').trim(),
      )?.phone ??
      (users ?? []).find(
        (user) =>
          String(user?.name ?? '').trim() ===
          String(matchedOrder?.customerName ?? '').trim(),
      )?.phone;

    return userPhone ?? undefined;
  };

  const applyOrderTemplateByField = (fieldName, rawValue) => {
    const selectedValue = String(rawValue ?? '').trim();
    if (!selectedValue) {
      return;
    }

    const matchedOrder = (orders ?? []).find(
      (order) => String(order?.[fieldName] ?? '').trim() === selectedValue,
    );

    if (!matchedOrder) {
      return;
    }

    form.setFieldsValue({
      customerName: matchedOrder.customerName ?? undefined,
      customerCompany: matchedOrder.customerCompany ?? undefined,
      customerPhone: resolveCustomerPhone(matchedOrder),
      deliveryName: matchedOrder.deliveryName ?? undefined,
      deliveryCompany: matchedOrder.deliveryCompany ?? undefined,
      deliveryPhone: matchedOrder.deliveryPhone ?? undefined,
      deliveryAddress: matchedOrder.deliveryAddress ?? undefined,
      deliveryAddressDetail: matchedOrder.deliveryAddressDetail ?? undefined,
      quantity: matchedOrder.quantity ?? undefined,
      orderDate: createOrderDateValue(),
    });

    setSearchTerms((prev) => ({
      ...prev,
      customerName: matchedOrder.customerName ?? '',
      customerCompany: matchedOrder.customerCompany ?? '',
      deliveryName: matchedOrder.deliveryName ?? '',
      deliveryCompany: matchedOrder.deliveryCompany ?? '',
    }));
  };

  const applyCustomerName = (rawValue) => {
    const selectedValue = String(rawValue ?? '').trim();
    if (!selectedValue) {
      return;
    }

    const matchedOrder = (orders ?? []).find(
      (order) => String(order?.customerName ?? '').trim() === selectedValue,
    );

    if (matchedOrder) {
      applyOrderTemplateByField('customerName', selectedValue);
      return;
    }

    const matchedUser = findCustomerUserByName(
      users,
      selectedValue,
      form.getFieldValue('customerCompany'),
    );

    form.setFieldsValue({
      customerName: selectedValue,
      customerCompany:
        form.getFieldValue('customerCompany') ||
        matchedUser?.customer?.name ||
        undefined,
      customerPhone: matchedUser?.phone || undefined,
    });

    setSearchTerms((prev) => ({
      ...prev,
      customerName: selectedValue,
      customerCompany:
        prev.customerCompany || matchedUser?.customer?.name || '',
    }));
  };

  const findAddress = () => {
    getAddress().then((data) => {
      form.setFieldValue('deliveryAddress', data.address);
    });
  };

  const doSave = (values) => {
    const data = {
      ...values,
      orderDate: formatOrderDateValue(values.orderDate),
      status: 'RECEIVED',
      quantity: Number(values.quantity),
    };

    API.post('/orders', data).then((response) => {
      const { success } = response;

      if (success) {
        message.success('저장을 완료하였습니다.');
        navigate('/orders');
      } else {
        message.error(getApiErrorMessage(response, '주문 저장을 실패했습니다.'));
      }
    });
  };

  const handleSearch = (key, value) => {
    setSearchTerms((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  const suggestionOptions = useMemo(
    () => ({
      customerCompany: buildCustomerCompanyOptions(
        customers,
        orders,
        searchTerms.customerCompany,
      ),
      customerName: buildCustomerNameOptions(
        users,
        orders,
        searchTerms.customerName,
      ),
      deliveryName: buildOrderFieldOptions(
        orders,
        'deliveryName',
        searchTerms.deliveryName,
      ),
      deliveryCompany: buildOrderFieldOptions(
        orders,
        'deliveryCompany',
        searchTerms.deliveryCompany,
      ),
    }),
    [customers, orders, searchTerms, users],
  );

  return (
    <div className={cx('container')}>
      <Space direction="vertical" size={16} className={cx('wrapper')}>
        <HeadingH1 title={'신규 주문'} />
        <Form layout="vertical" onFinish={doSave} form={form} className={cx('form')}>
          <Typography.Text className={cx('title')} strong>
            주문정보
          </Typography.Text>
          <Row align={'top'} style={{ maxWidth: 640 }} justify={'space-between'}>
            <Space direction="vertical" style={{ width: '48%' }}>
              <Form.Item
                name={'orderNo'}
                label="주문번호"
              >
                <Input disabled placeholder="저장 시 자동 발급" />
              </Form.Item>
              <Form.Item
                name={'originNo'}
                label="원천 시스템 주문번호"
              >
                <Input placeholder="외부 연동 주문인 경우에만 입력" />
              </Form.Item>
              <Form.Item name={'quantity'} label="수량" rules={[{ required: true, message: '수량을 입력해주세요' }]}>
                <InputNumber />
              </Form.Item>
              <Form.Item
                name={'customerName'}
                label="주문자명"
                rules={[{ required: true, message: '주문자명을 입력해주세요' }]}>
                <AutoComplete
                  options={suggestionOptions.customerName}
                  onSearch={(value) => handleSearch('customerName', value)}
                  onChange={(value) => handleSearch('customerName', value)}
                  onSelect={(value) => applyCustomerName(value)}
                  filterOption={false}>
                  <Input
                    onKeyDown={createAutocompleteKeyDownHandler(
                      form,
                      'customerName',
                      suggestionOptions.customerName,
                      (value) => applyCustomerName(value),
                    )}
                  />
                </AutoComplete>
              </Form.Item>
            </Space>
            <Space direction="vertical" style={{ width: '48%' }}>
              <Form.Item
                name={'orderDate'}
                label="주문일자"
                rules={[{ required: true, message: '주문일자를 선택해주세요' }]}>
                <DatePicker placeholder="주문일자 선택" />
              </Form.Item>
              <Form.Item
                name={'customerCompany'}
                label="주문자 회사명"
                rules={[{ required: true, message: '회사명을 입력해주세요' }]}>
                <AutoComplete
                  options={suggestionOptions.customerCompany}
                  onSearch={(value) => handleSearch('customerCompany', value)}
                  onChange={(value) => handleSearch('customerCompany', value)}
                  onSelect={(value) =>
                    applyOrderTemplateByField('customerCompany', value)
                  }
                  filterOption={false}>
                  <Input
                    onKeyDown={createAutocompleteKeyDownHandler(
                      form,
                      'customerCompany',
                      suggestionOptions.customerCompany,
                      (value) =>
                        applyOrderTemplateByField('customerCompany', value),
                    )}
                  />
                </AutoComplete>
              </Form.Item>
              <Form.Item
                name={'customerPhone'}
                label="주문자 전화번호"
                rules={[{ required: true, message: '전화번호를 입력해주세요' }]}>
                <Input />
              </Form.Item>
            </Space>
          </Row>
          <Divider />
          <Typography.Text className={cx('title')} strong>
            배송정보
          </Typography.Text>
          <Row align={'top'} style={{ maxWidth: 640 }} justify={'space-between'}>
            <Space style={{ width: '48%' }} direction="vertical">
              <Form.Item
                name={'deliveryName'}
                label="수신자명"
                rules={[{ required: true, message: '수신자명을 입력해주세요' }]}>
                <AutoComplete
                  options={suggestionOptions.deliveryName}
                  onSearch={(value) => handleSearch('deliveryName', value)}
                  onChange={(value) => handleSearch('deliveryName', value)}
                  onSelect={(value) =>
                    applyOrderTemplateByField('deliveryName', value)
                  }
                  filterOption={false}>
                  <Input
                    onKeyDown={createAutocompleteKeyDownHandler(
                      form,
                      'deliveryName',
                      suggestionOptions.deliveryName,
                      (value) => applyOrderTemplateByField('deliveryName', value),
                    )}
                  />
                </AutoComplete>
              </Form.Item>
              <Form.Item
                name={'deliveryCompany'}
                label="수신자 회사명"
                rules={[{ required: true, message: '회사명을 입력해주세요' }]}>
                <AutoComplete
                  options={suggestionOptions.deliveryCompany}
                  onSearch={(value) => handleSearch('deliveryCompany', value)}
                  onChange={(value) => handleSearch('deliveryCompany', value)}
                  onSelect={(value) =>
                    applyOrderTemplateByField('deliveryCompany', value)
                  }
                  filterOption={false}>
                  <Input
                    onKeyDown={createAutocompleteKeyDownHandler(
                      form,
                      'deliveryCompany',
                      suggestionOptions.deliveryCompany,
                      (value) =>
                        applyOrderTemplateByField('deliveryCompany', value),
                    )}
                  />
                </AutoComplete>
              </Form.Item>
            </Space>
            <Space style={{ width: '48%' }} direction="vertical">
              <Form.Item
                name={'deliveryPhone'}
                label="수신자 전화번호"
                rules={[{ required: true, message: '전화번호를 입력해주세요' }]}>
                <Input style={{ width: 240 }} />
              </Form.Item>
              <Form.Item
                name={'deliveryAddress'}
                label="주소"
                rules={[{ required: true, message: '주소를 입력해주세요' }]}>
                <Input.Search onClick={findAddress} onSearch={findAddress} />
              </Form.Item>
              <Form.Item name={'deliveryAddressDetail'} label="상세주소">
                <Input />
              </Form.Item>
            </Space>
          </Row>
          <Typography.Text className={cx('title')} strong>
            부가정보
          </Typography.Text>
          <Form.Item name={'memo'} label="메모">
            <Input.TextArea style={{ resize: 'none', maxWidth: 840, minHeight: 150 }} />
          </Form.Item>

          <BottomButtons
            left={[
              <Button key={'backPage'} onClick={() => navigate(-1)}>
                뒤로
              </Button>,
            ]}
            right={[
              <Button key={'saveOrder'} type="primary" htmlType="submit">
                저장
              </Button>,
            ]}
          />
        </Form>
      </Space>
    </div>
  );
};

export default Page;
